export type FieldDecisionValue =
  | 'Migrate' | 'Migrate with Change' | "Don't Migrate" | 'Needs Further Discussion' | ''

export type FieldDecision = {
  id: string
  object: string
  category: string
  fieldName: string
  kind: string
  choices: string
  fillFrequency: string
  usagePct: number | null
  recordsWithData: number | null
  recordsTotal: number | null
  usedIn: string[]
  usedInSummary: string
  suggestion: string
  decision: FieldDecisionValue
  ifChanging: string
  notes: string
  apiName: string
  decided: boolean
  /** The workbook answers this field twice with different verdicts. */
  conflict?: boolean
  conflictOptions?: string[]
  decidedBy?: string | null
  decidedAt?: { seconds: number } | null
  /**
   * How many distinct dropdown options this field has, and how many have been answered.
   * Kept here rather than counted from `choiceDecisions` so the picklist review can show
   * progress for every field at once without reading five thousand choices to do it.
   */
  optionCount?: number
  optionsDecided?: number
  /** Where a migrating field's build stands — To Build, Built or Open. See `buildStatus.ts`. */
  buildStatus?: string
  /** The new-org field it was built as, i.e. where it lives now: `Account.Customer_Status__c`. */
  builtAs?: string
  buildStatusBy?: string | null
  buildStatusAt?: { seconds: number } | null
}

export const FIELD_DECISIONS: FieldDecisionValue[] = [
  'Migrate', 'Migrate with Change', "Don't Migrate", 'Needs Further Discussion',
]

export const toneForDecision = (d: string) =>
  d === 'Migrate' ? 'done'
  : d === 'Migrate with Change' ? 'primary'
  : d === "Don't Migrate" ? 'neutral'
  : d === 'Needs Further Discussion' ? 'critical'
  : 'high'

/**
 * A field nobody fills in still cannot simply be dropped if something references it.
 * This is the case the sheet's "Starting suggestion" column cannot see on its own, and
 * it is the one worth surfacing loudly.
 */
export const isUnusedButReferenced = (f: FieldDecision) =>
  (f.usagePct ?? 0) === 0 && f.usedIn.length > 0

/**
 * Everything a field row can be searched by, in one string.
 *
 * The search box over both field tables promises "Field name or API name", but a table's
 * global filter only sees the values its columns expose, and the API name is printed
 * inside the field-name cell rather than given a column of its own — so pasting
 * `Follow_Up_Date_New__c` matched nothing while its label matched fine. Used as the
 * column's accessor, it leads with the label so the column still reads and sorts by the
 * name, and carries the API name behind it for the filter to find — and, once the field is
 * built, the new-org field it was built as.
 */
export const fieldSearchText = (f: FieldDecision): string =>
  [f.fieldName, f.apiName, f.builtAs]
    .map((v) => (v ?? '').trim())
    .filter((v, i, all) => v !== '' && all.indexOf(v) === i)
    .join(' ')

export type ChoiceSummary = { total: number; decided: number }

/**
 * How many dropdown options there are and how many are answered — summed from the field
 * rows already on screen.
 *
 * This has been three things. It was a listener over the whole of `choiceDecisions`, which
 * was affordable while a filtered export had left 355 of them in the database and
 * indefensible once the real 16,866 arrived. It was then a pair of count queries, which
 * looked like the frugal answer and was not: Firestore bills an aggregate for every batch
 * of a thousand index entries it scans, so counting a collection of 16,866 costs seventeen
 * reads to return a single integer, and `getCountFromServer` is answered by the server
 * every time — the on-device cache cannot serve it, the shared store cannot share it, and
 * nothing resumes it. Thirty-six visits to this page cost 576 reads to print one sentence,
 * which made it the most expensive thing in the app per unit of information delivered, and
 * the only read path left that caching could not touch.
 *
 * The numbers were already here. `optionCount` and `optionsDecided` are maintained on each
 * field by `setOptionDecision`, `addOptionValue` and `removeAddedValue`, every one of them
 * by `increment`, and the field rows are delivered by a listener that a warm cache resumes
 * for nothing. Summing them costs no reads at all and is live besides: the count queries
 * were a snapshot taken at mount and never refreshed, so answering an option left the
 * sentence stale until somebody reloaded.
 *
 * It counts distinct options rather than the 16,866 `choiceDecisions` rows, and that is the
 * more honest number: a value carried by five record types is five rows but one question,
 * and `setOptionDecision` answers all five at once. It is also the number the picklist
 * review already shows, which the row count never agreed with.
 */
export const optionProgress = (fields: FieldDecision[]): ChoiceSummary =>
  fields.reduce(
    (sum, f) => ({
      total: sum.total + (f.optionCount ?? 0),
      decided: sum.decided + (f.optionsDecided ?? 0),
    }),
    { total: 0, decided: 0 },
  )
